import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X, Upload, AlertCircle, Image as ImageIcon, Edit2 } from "lucide-react";
import { formatDate, relTime } from "../lib/date";

export default function AdminNotices() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | notice object

  const load = () => api.get("/notices").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this notice?")) return;
    try { await api.delete(`/notices/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-4 sm:p-8">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="mb-1 text-[11px] uppercase tracking-widest text-zinc-500">Communications</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-black">Notices</h1>
        </div>
        <button data-testid="new-notice-btn" onClick={() => setEditing("new")}
          className="inline-flex items-center gap-1.5 rounded-sm bg-black px-3 py-2 text-xs font-medium text-white hover:bg-zinc-800">
          <Plus className="h-3.5 w-3.5" /> Create Notice
        </button>
      </div>

      <div className="overflow-x-auto rounded-sm border border-zinc-200 bg-white">
        <table className="w-full text-sm min-w-[600px]" data-testid="notices-table">
          <thead className="bg-zinc-50 text-left text-[11px] uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="px-4 py-3">Notice</th>
              <th className="px-3 py-3">Priority</th>
              <th className="px-3 py-3">Status</th>
              <th className="px-3 py-3">Reads</th>
              <th className="px-3 py-3">Published</th>
              <th className="px-3 py-3 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-sm text-zinc-500">No notices yet. Click "Create Notice" to publish your first.</td></tr>}
            {items.map(n => (
              <tr key={n.id} className="border-t border-zinc-200 hover:bg-zinc-50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    {n.image && <ImageIcon className="h-3.5 w-3.5 text-zinc-400 shrink-0"/>}
                    <div>
                      <div className="font-medium text-black">{n.title}</div>
                      <div className="text-[11px] text-zinc-500 line-clamp-1 max-w-md">{n.message}</div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3">
                  {n.priority === "important"
                    ? <span className="inline-flex items-center gap-1 rounded-sm bg-red-50 px-1.5 py-0.5 text-[11px] font-medium text-red-700"><AlertCircle className="h-3 w-3"/>Important</span>
                    : <span className="text-[11px] text-zinc-500">Normal</span>}
                </td>
                <td className="px-3 py-3">
                  {n.status === "active"
                    ? <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500"/>Active</span>
                    : <span className="text-[11px] text-zinc-400">Inactive</span>}
                </td>
                <td className="px-3 py-3 tabular-nums text-xs">{n.read_count}</td>
                <td className="px-3 py-3 text-[11px] text-zinc-500" title={formatDate(n.created_at)}>{relTime(n.created_at)}</td>
                <td className="px-3 py-3 text-right whitespace-nowrap">
                  <button data-testid={`edit-notice-${n.id}`} onClick={() => setEditing(n)} className="rounded-sm p-1 text-zinc-500 hover:bg-zinc-100"><Edit2 className="h-3.5 w-3.5"/></button>
                  <button data-testid={`delete-notice-${n.id}`} onClick={() => del(n.id)} className="rounded-sm p-1 text-zinc-500 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-3.5 w-3.5"/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <NoticeEditor
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function NoticeEditor({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: initial?.title || "",
    message: initial?.message || "",
    image: initial?.image || "",
    priority: initial?.priority || "normal",
    status: initial?.status || "active",
  });
  const [saving, setSaving] = useState(false);

  const handleFile = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 1_200_000) return toast.error("Image must be under 1MB");
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, image: reader.result }));
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (initial) await api.patch(`/notices/${initial.id}`, form);
      else await api.post("/notices", form);
      toast.success(initial ? "Notice updated" : "Notice published");
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-sm border border-zinc-200 bg-white p-5 sm:p-6 max-h-[92vh] overflow-y-auto">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-zinc-500">{initial ? "Edit" : "New"}</div>
            <h3 className="font-heading text-xl font-bold text-black">{initial ? "Update Notice" : "Create Notice"}</h3>
          </div>
          <button type="button" onClick={onClose} className="rounded-sm p-1 hover:bg-zinc-100"><X className="h-4 w-4"/></button>
        </div>
        <div className="mb-3">
          <label className="mb-1 block text-[11px] uppercase tracking-wider text-zinc-500">Title *</label>
          <input data-testid="notice-title" required value={form.title} onChange={(e) => setForm({...form, title: e.target.value})}
            className="w-full rounded-sm border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-black" />
        </div>
        <div className="mb-3">
          <label className="mb-1 block text-[11px] uppercase tracking-wider text-zinc-500">Message *</label>
          <textarea data-testid="notice-message" required rows={5} value={form.message} onChange={(e) => setForm({...form, message: e.target.value})}
            className="w-full rounded-sm border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-black" />
        </div>
        <div className="mb-3 grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-[11px] uppercase tracking-wider text-zinc-500">Priority</label>
            <select data-testid="notice-priority" value={form.priority} onChange={(e) => setForm({...form, priority: e.target.value})}
              className="w-full rounded-sm border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-black">
              <option value="normal">Normal</option>
              <option value="important">Important</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-[11px] uppercase tracking-wider text-zinc-500">Status</label>
            <select data-testid="notice-status" value={form.status} onChange={(e) => setForm({...form, status: e.target.value})}
              className="w-full rounded-sm border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-black">
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
        <div className="mb-4">
          <label className="mb-1 block text-[11px] uppercase tracking-wider text-zinc-500">Image (optional, max 1MB)</label>
          <div className="flex items-center gap-2">
            <label className="cursor-pointer inline-flex items-center gap-1.5 rounded-sm border border-zinc-300 bg-white px-3 py-2 text-xs font-medium hover:bg-zinc-50">
              <Upload className="h-3.5 w-3.5"/> {form.image ? "Replace" : "Upload"}
              <input data-testid="notice-image-input" type="file" accept="image/*" className="hidden" onChange={handleFile} />
            </label>
            {form.image && <button type="button" onClick={() => setForm({...form, image: ""})}
              className="text-[11px] text-red-600 underline">Remove</button>}
          </div>
          {form.image && <img src={form.image} alt="preview" className="mt-3 max-h-48 rounded-sm border border-zinc-200 object-contain" />}
        </div>
        <button data-testid="notice-submit" disabled={saving} type="submit"
          className="w-full rounded-sm bg-black px-4 py-2.5 text-sm font-semibold text-white hover:bg-zinc-800 disabled:opacity-60">
          {saving ? "Saving…" : (initial ? "Save changes" : "Publish notice")}
        </button>
      </form>
    </div>
  );
}
